import React, { useEffect, useMemo, useRef, useCallback, useState } from "react";
import classNames from "classnames";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";

import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import { columnResizing, tableEditing } from "@tiptap/pm/tables";
import { Node } from "@tiptap/core";

import { Button, Dropdown, Modal, Input, Space, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  UndoOutlined,
  RedoOutlined,
  LinkOutlined,
  DisconnectOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  TableOutlined,
  MergeCellsOutlined,
  BorderOutlined,
  InsertRowBelowOutlined,
  InsertRowAboveOutlined,
  InsertRowRightOutlined,
  InsertRowLeftOutlined,
  DeleteRowOutlined,
  DeleteColumnOutlined,
  DeleteOutlined,
  ColumnWidthOutlined,
} from "@ant-design/icons";

import ImageUpload from "./ImageUpload";
import VideoUpload from "./VideoUpload";

import "./editor.css";

const ResizableTable = Table.extend({
  addProseMirrorPlugins() {
    return [
      ...(this.options.resizable
        ? [
            columnResizing({
              handleWidth: this.options.handleWidth,
              cellMinWidth: this.options.cellMinWidth,
              defaultCellMinWidth: this.options.cellMinWidth,
              View: this.options.View,
              lastColumnResizable: this.options.lastColumnResizable,
            }),
          ]
        : []),
      tableEditing({
        allowTableNodeSelection: this.options.allowTableNodeSelection,
      }),
    ];
  },
});

const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      poster: { default: null },
      title: { default: null },
      "data-id": { default: null },
      id: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: "video" }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes };

    if (attrs.controls === true) attrs.controls = "controls";
    if (attrs.controls === false) delete attrs.controls;

    if (!attrs.style) attrs.style = "max-width: 100%; height: auto;";
    return ["video", attrs];
  },

  addCommands() {
    return {
      setVideo:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

function isProbablyHtml(s) {
  return /<(p|h1|h2|h3|h4|table|thead|tbody|tr|td|th|div|span|img|video|ul|ol|li|strong|em|a|br|hr)\b/i.test(
    String(s || "")
  );
}

function coerceToHtml(content) {
  if (content == null) return "";
  const s = String(content);
  if (!s.trim()) return "";
  if (isProbablyHtml(s)) return s;

  const escaped = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r/g, "")
    .replace(/\n{2,}/g, "</p><p>")
    .replace(/\n/g, "<br/>");

  return `<p>${escaped}</p>`;
}

function findScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return window;
}

function IconBtn({ title, onClick, active, disabled, icon, danger }) {
  return (
    <Tooltip title={title}>
      <Button
        danger={danger}
        type="text"
        className={classNames("lf-editor__iconbtn", { "is-active": active })}
        onClick={onClick}
        disabled={disabled}
        icon={icon}
      />
    </Tooltip>
  );
}

function ToolbarDivider() {
  return <span className="lf-editor__divider" />;
}

export const Editor = ({
  content,
  readonly,
  onChange,
  articleId,
  legalDocumentId,
  // a task description owns its media the same way an article does (LF360-1852)
  dailyTaskId,
  disableImageUpload = false,
  disableVideoUpload = false,
}) => {
  const isReadOnly = !!readonly;
  const [isInTable, setIsInTable] = useState(false);
  const rootRef = useRef(null);
  const sentinelRef = useRef(null);

  const suppressUpdateRef = useRef(false);

  // link modal state
  const [isLinkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  // toolbar shadow state
  const [hasShadow, setHasShadow] = useState(false);

  const editor = useEditor({
    editable: !isReadOnly,
    content: coerceToHtml(content),
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow" },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder: isReadOnly ? "" : "Type here…",
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { style: "max-width: 100%; height: auto;" },
      }),
      Video,
      ResizableTable.configure({ resizable: true, handleWidth: 10, cellMinWidth: 80 }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    onUpdate: ({ editor }) => {
      if (suppressUpdateRef.current) return;
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    const update = () => {
      setIsInTable(editor.isActive("table"));
    };

    editor.on("selectionUpdate", update);
    editor.on("transaction", update);

    update();

    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  // Sync external content -> editor for read-only/imported renders.
  //
  // In edit mode Tiptap must stay uncontrolled after initial mount. The parent
  // updates `content` on every keystroke and again when autosave echoes through
  // the article state. If we call setContent for those snapshots, a stale React
  // render can overwrite newer editor text and reset the selection to the end
  // of the current section/table.
  useEffect(() => {
    if (!editor) return;
    if (!isReadOnly) return;

    const next = coerceToHtml(content);
    const current = editor.getHTML();
    if (String(current || "") === String(next || "")) return;

    suppressUpdateRef.current = true;
    editor.commands.setContent(next, false);
    Promise.resolve().then(() => {
      suppressUpdateRef.current = false;
    });
  }, [editor, content]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isReadOnly);
  }, [editor, isReadOnly]);

  // sticky shadow via IntersectionObserver
  useEffect(() => {
    if (isReadOnly) return;
    const root = rootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const scrollParent = findScrollParent(root);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHasShadow(!entry.isIntersecting);
      },
      {
        root: scrollParent === window ? null : scrollParent,
        threshold: 1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isReadOnly]);

  const canAct = !!editor && !isReadOnly;
  // const isInTable = !!editor?.isActive("table");

  // ---------- block style dropdown ----------
  const blockLabel = useMemo(() => {
    if (!editor) return "Body";
    if (editor.isActive("heading", { level: 1 })) return "H1";
    if (editor.isActive("heading", { level: 2 })) return "H2";
    if (editor.isActive("heading", { level: 3 })) return "H3";
    return "Body";
  }, [editor, editor?.state]);

  const blockMenuItems = useMemo(
    () => [
      { key: "p", label: "Body", onClick: () => editor?.chain().focus().setParagraph().run() },
      { key: "h1", label: "Heading 1", onClick: () => editor?.chain().focus().toggleHeading({ level: 1 }).run() },
      { key: "h2", label: "Heading 2", onClick: () => editor?.chain().focus().toggleHeading({ level: 2 }).run() },
      { key: "h3", label: "Heading 3", onClick: () => editor?.chain().focus().toggleHeading({ level: 3 }).run() },
    ],
    [editor]
  );

  // ---------- link ----------
  const openLinkModal = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link")?.href || "";
    setLinkUrl(prev);
    setLinkOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = String(linkUrl || "").trim();

    if (!url) {
      editor.chain().focus().unsetLink().run();
      setLinkOpen(false);
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    setLinkOpen(false);
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  // ---------- table commands ----------
  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  // ---------- upload integration ----------
  const handleInsertHtml = useCallback(
    (html) => {
      if (!editor || !html) return;
      editor.chain().focus().insertContent(html).run();
    },
    [editor]
  );

  const handleInsertImage = useCallback(
    ({ src, alt, uploadId }) => {
      if (!editor || !src) return;
      editor
        .chain()
        .focus()
        .setImage({
          src,
          alt: alt || (uploadId ? `image-${uploadId}` : ""),
          title: uploadId ? `Image ${uploadId}` : undefined,
          id: uploadId || undefined,
          "data-id": uploadId || undefined,
        })
        .run();
    },
    [editor]
  );

  const handleInsertVideo = useCallback(
    ({ src, poster, uploadId }) => {
      if (!editor || !src) return;
      editor
        .chain()
        .focus()
        .setVideo({
          src,
          poster: poster || null,
          controls: true,
          id: uploadId || undefined,
          "data-id": uploadId || undefined,
          title: uploadId ? `Video ${uploadId}` : undefined,
        })
        .run();
    },
    [editor]
  );

  return (
    <div ref={rootRef} className={classNames("lf-editor", { "lf-editor__readonly": isReadOnly })}>
      {!isReadOnly ? (
        <div className={classNames("lf-editor__toolbar", { "lf-editor__toolbar--shadow": hasShadow })}>
          {/* main row */}
          <div className="lf-editor__toolbarRow">
            <Space size={6} wrap>
              <Dropdown
                menu={{
                  items: blockMenuItems.map((it) => ({
                    key: it.key,
                    label: it.label,
                    onClick: it.onClick,
                  })),
                }}
                trigger={["click"]}
              >
                <Button className="lf-editor__blockSelect" size="middle">
                  {blockLabel}
                </Button>
              </Dropdown>

              <ToolbarDivider />

              <IconBtn
                title="Bold"
                disabled={!canAct}
                active={editor?.isActive("bold")}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                icon={<BoldOutlined />}
              />
              <IconBtn
                title="Italic"
                disabled={!canAct}
                active={editor?.isActive("italic")}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                icon={<ItalicOutlined />}
              />
              <IconBtn
                title="Underline"
                disabled={!canAct}
                active={editor?.isActive("underline")}
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                icon={<UnderlineOutlined />}
              />

              <ToolbarDivider />

              <IconBtn
                title="Bullet list"
                disabled={!canAct}
                active={editor?.isActive("bulletList")}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                icon={<UnorderedListOutlined />}
              />
              <IconBtn
                title="Ordered list"
                disabled={!canAct}
                active={editor?.isActive("orderedList")}
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                icon={<OrderedListOutlined />}
              />

              <ToolbarDivider />

              <IconBtn
                title="Align left"
                disabled={!canAct}
                active={editor?.isActive({ textAlign: "left" })}
                onClick={() => editor?.chain().focus().setTextAlign("left").run()}
                icon={<AlignLeftOutlined />}
              />
              <IconBtn
                title="Align center"
                disabled={!canAct}
                active={editor?.isActive({ textAlign: "center" })}
                onClick={() => editor?.chain().focus().setTextAlign("center").run()}
                icon={<AlignCenterOutlined />}
              />
              <IconBtn
                title="Align right"
                disabled={!canAct}
                active={editor?.isActive({ textAlign: "right" })}
                onClick={() => editor?.chain().focus().setTextAlign("right").run()}
                icon={<AlignRightOutlined />}
              />

              <ToolbarDivider />

              <IconBtn
                title="Insert/Edit link"
                disabled={!canAct}
                active={editor?.isActive("link")}
                onClick={openLinkModal}
                icon={<LinkOutlined />}
              />
              <IconBtn
                title="Remove link"
                disabled={!canAct}
                active={false}
                onClick={removeLink}
                icon={<DisconnectOutlined />}
              />

              <ToolbarDivider />

              <IconBtn
                title="Undo"
                disabled={!canAct || !editor?.can().undo()}
                active={false}
                onClick={() => editor?.chain().focus().undo().run()}
                icon={<UndoOutlined />}
              />
              <IconBtn
                title="Redo"
                disabled={!canAct || !editor?.can().redo()}
                active={false}
                onClick={() => editor?.chain().focus().redo().run()}
                icon={<RedoOutlined />}
              />

              <ToolbarDivider />

              <IconBtn
                title="Insert table"
                disabled={!canAct}
                active={isInTable}
                onClick={insertTable}
                icon={<TableOutlined />}
              />

              {!disableImageUpload && (
                <ImageUpload
                  articleId={articleId}
                  legalDocumentId={legalDocumentId}
                  dailyTaskId={dailyTaskId}
                  onInsertHtml={handleInsertHtml}
                  onInsertImage={handleInsertImage}
                  disabled={!canAct}
                />
              )}

              {!disableVideoUpload && (
                <VideoUpload
                  articleId={articleId}
                  legalDocumentId={legalDocumentId}
                  dailyTaskId={dailyTaskId}
                  onInsertHtml={handleInsertHtml}
                  onInsertVideo={handleInsertVideo}
                  disabled={!canAct}
                />
              )}
            </Space>
          </div>

          {/* secondary row: table tools (only inside table) */}
          <div
            className={classNames(
              "lf-editor__toolbarRow",
              "lf-editor__toolbarRow--secondary",
              { "is-hidden": !canAct || !isInTable }
            )}
          >
            <Space size={4} wrap>
              <IconBtn
                title="Add row above"
                disabled={!canAct}
                icon={<InsertRowAboveOutlined />}
                onClick={() => editor.chain().focus().addRowBefore().run()}
              />
              <IconBtn
                title="Add row below"
                disabled={!canAct}
                icon={<InsertRowBelowOutlined />}
                onClick={() => editor.chain().focus().addRowAfter().run()}
              />
              <IconBtn
                title="Add column left"
                disabled={!canAct}
                icon={<InsertRowLeftOutlined />}
                onClick={() => editor.chain().focus().addColumnBefore().run()}
              />
              <IconBtn
                title="Add column right"
                disabled={!canAct}
                icon={<InsertRowRightOutlined />}
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              />

              <span className="lf-editor__bubbleDivider" />

              <IconBtn
                title="Merge cells"
                disabled={!canAct}
                icon={<MergeCellsOutlined />}
                onClick={() => editor.chain().focus().mergeCells().run()}
              />
              <IconBtn
                title="Split cell"
                disabled={!canAct}
                icon={<BorderOutlined />}
                onClick={() => editor.chain().focus().splitCell().run()}
              />

              <span className="lf-editor__bubbleDivider" />

              <IconBtn
                title="Delete row"
                disabled={!canAct}
                icon={<DeleteRowOutlined />}
                onClick={() => editor.chain().focus().deleteRow().run()}
              />
              <IconBtn
                title="Delete column"
                disabled={!canAct}
                icon={<DeleteColumnOutlined />}
                onClick={() => editor.chain().focus().deleteColumn().run()}
              />
              <IconBtn
                title="Delete table"
                danger
                disabled={!canAct}
                icon={<DeleteOutlined />}
                onClick={() => editor.chain().focus().deleteTable().run()}
              />

              <span className="lf-editor__bubbleDivider" />

              <IconBtn
                title="Toggle header row"
                disabled={!canAct}
                icon={<ColumnWidthOutlined />}
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              />
            </Space>
          </div>
        </div>
      ) : null}

      <div className="lf-editor__content">
        {!isReadOnly ? <div ref={sentinelRef} className="lf-editor__sentinel" /> : null}

        <EditorContent editor={editor} className="lf-editor__contentInner" />
      </div>

      <Modal
        title="Link"
        open={isLinkOpen}
        onCancel={() => setLinkOpen(false)}
        onOk={applyLink}
        okText="Apply"
        cancelText="Cancel"
        destroyOnHidden
      >
        <div style={{ display: "grid", gap: 10 }}>
          <Input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            onPressEnter={applyLink}
            autoFocus
          />
          <div style={{ opacity: 0.7, fontSize: 12 }}>
            Tip: щоб прибрати лінк — залиш поле пустим і натисни Apply.
          </div>
        </div>
      </Modal>
    </div>
  );
};
